// Real-time MinSAS sonar mosaic editor (Phase 1): load several per-ping-tile survey lines,
// select one or many, adjust opacity / across-track range trim live in a WebGL preview, and
// bake the result into a real mosaic GeoTIFF via the server's proven alpha-band compositing
// pipeline (see runMinsasMosaicComposeJob in services/ais-api/src/server.js).
//
// Phase 1 scope: line selection (single + multi via shift/ctrl-click), per-line opacity,
// per-line across-track range trim (in/out), draw-order-based "over" blending, bake-to-GeoTIFF.
// Phase 2 (not here): a TVG-approximation gain curve, "average"/"max" blend modes -- see
// /home/ubuntu/.claude/plans/quizzical-conjuring-axolotl.md for why those need a materially
// different render/bake path, not just a new parameter on this one.
//
// Architecture notes (see the plan file for the full investigation this is based on):
// - No client-side GeoTIFF reader exists in this repo, so the server pre-converts each tile to
//   a downsampled preview PNG (GET .../tile-preview) and ships its real geometry as JSON
//   (GET .../line-tiles) -- this tool positions each tile as a textured quad using that
//   geometry, it never parses a GeoTIFF itself.
// - World space is local tangent-plane meters centered on the first loaded line's centroid
//   (equirectangular approximation -- accurate to well under a pixel at survey-line scale),
//   same "local frame relative to a center point" convention the rest of this app uses.
// - The canvas is mounted once and never torn down by a innerHTML re-render (unlike the rest of
//   this tool's control panel, which freely replaces its own innerHTML on every render() --
//   that would destroy/recreate the WebGL context on every slider tick otherwise).

import * as THREE from "three";

const TOOL_ID = "sonar-mosaic-editor";
const MINSAS_FOLDER_PREFIX = "Minsas/";
const DEG2RAD = Math.PI / 180;

let _lastBroadcastProjectId = "";
document.addEventListener("hydrotools:project-changed", (e) => {
  _lastBroadcastProjectId = String(e.detail?.projectId || "");
});

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function toLocalXY(lonLat, center) {
  const dx = (lonLat[0] - center[0]) * 111320 * Math.cos(center[1] * DEG2RAD);
  const dy = (lonLat[1] - center[1]) * 110540;
  return [dx, dy];
}

// One row step (across-track) in meters, from the tile's own GeoTransform -- recomputed per
// tile, never assumed uniform per line, since adjacent pings can have slightly different
// headings/pixel spacing.
// The across-track axis is the tile's COLUMN direction (width ≈11300px; height ≈1536px is
// along-track, the ping-sequence/forward-motion axis) -- confirmed visually, the nadir gap
// runs as a band across the width axis in the un-rotated preview, splitting port/starboard
// left-right, not top-bottom. One column step moves world position by (GT[1], GT[4]).
function metersPerAcrossTrackPixel(geometry) {
  const gt = geometry.geoTransform;
  const c = geometry.cornerLonLat;
  if (!gt || !c?.ul || !c?.ur) return null;
  const lat0 = (c.ul[1] + c.ur[1]) / 2;
  const dxM = gt[1] * 111320 * Math.cos(lat0 * DEG2RAD);
  const dyM = gt[4] * 110540;
  return Math.sqrt(dxM * dxM + dyM * dyM) || null;
}

function createRuntime(app) {
  let containerEl = null;
  let canvasHostEl = null;
  let currentCode = "";

  // Three.js scene, built once per mount
  let renderer = null, scene = null, camera = null, controls = null;
  let rafId = null;
  let resizeObs = null;

  let localCenter = null; // [lon, lat] -- set from the first tile of the first loaded line
  let availableFolders = []; // Minsas line-folder names found under source-data
  let loadingFolders = false;

  // lines: [{ folder, included, opacity, trimNearM, trimFarM, order, loading, error, tiles, maxTrimM }]
  // tiles (per line): [{ key, geometry, mesh, baseCorners: {ul,ur,ll,lr} (local XY), metersPerPx }]
  let lines = [];
  let selectedLineIds = new Set();
  let lastClickedLineId = null;

  let trimUpdateQueued = false;
  let lastTrimUpdateAt = 0;

  let bakeJobId = null;
  let bakeStatus = null;
  let bakePollTimer = null;
  let bakeName = "";

  function findLine(folder) { return lines.find((l) => l.folder === folder); }
  function selectedLines() { return lines.filter((l) => selectedLineIds.has(l.folder)); }

  // ── Three.js scene lifecycle ────────────────────────────────────────────────────────────
  function ensureScene() {
    if (renderer) return;
    if (!canvasHostEl) return;
    const w = Math.max(200, Math.floor(canvasHostEl.clientWidth || 640));
    const h = Math.max(200, Math.floor(canvasHostEl.clientHeight || 480));

    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.setSize(w, h, false);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    canvasHostEl.appendChild(renderer.domElement);

    scene = new THREE.Scene();
    const viewHalfHeight = 50; // meters -- fitToTiles() below sets the real extent once data loads
    camera = new THREE.OrthographicCamera(-viewHalfHeight, viewHalfHeight, viewHalfHeight, -viewHalfHeight, 0.1, 10000);
    camera.position.set(0, 0, 100);
    camera.lookAt(0, 0, 0);

    setupPanZoom();

    if (typeof ResizeObserver !== "undefined") {
      resizeObs = new ResizeObserver(() => {
        if (!renderer || !camera || !canvasHostEl) return;
        const nw = Math.max(200, Math.floor(canvasHostEl.clientWidth || 640));
        const nh = Math.max(200, Math.floor(canvasHostEl.clientHeight || 480));
        renderer.setSize(nw, nh, false);
        const aspect = nw / nh;
        const halfH = (camera.top - camera.bottom) / 2;
        camera.left = -halfH * aspect; camera.right = halfH * aspect;
        camera.updateProjectionMatrix();
      });
      resizeObs.observe(canvasHostEl);
    }

    const loop = () => {
      rafId = requestAnimationFrame(loop);
      const now = performance.now();
      if (trimUpdateQueued && now - lastTrimUpdateAt > 100) {
        trimUpdateQueued = false;
        lastTrimUpdateAt = now;
        applyTrimGeometry();
      }
      renderer.render(scene, camera);
    };
    rafId = requestAnimationFrame(loop);
  }

  function setupPanZoom() {
    const el = renderer.domElement;
    let dragging = false, lastX = 0, lastY = 0;
    el.addEventListener("pointerdown", (ev) => { dragging = true; lastX = ev.clientX; lastY = ev.clientY; el.setPointerCapture(ev.pointerId); });
    el.addEventListener("pointermove", (ev) => {
      if (!dragging) return;
      const halfH = (camera.top - camera.bottom) / 2;
      const scale = (halfH * 2) / canvasHostEl.clientHeight;
      camera.position.x -= (ev.clientX - lastX) * scale;
      camera.position.y += (ev.clientY - lastY) * scale;
      lastX = ev.clientX; lastY = ev.clientY;
    });
    el.addEventListener("pointerup", (ev) => { dragging = false; try { el.releasePointerCapture(ev.pointerId); } catch {} });
    el.addEventListener("wheel", (ev) => {
      ev.preventDefault();
      const factor = ev.deltaY > 0 ? 1.1 : 0.9;
      const cx = (camera.left + camera.right) / 2, cy = (camera.top + camera.bottom) / 2;
      const w = (camera.right - camera.left) * factor / 2, h = (camera.top - camera.bottom) * factor / 2;
      camera.left = cx - w; camera.right = cx + w; camera.top = cy + h; camera.bottom = cy - h;
      camera.updateProjectionMatrix();
    }, { passive: false });
  }

  function fitToTiles() {
    if (!camera || !canvasHostEl) return;
    const box = new THREE.Box3();
    let any = false;
    for (const line of lines) for (const t of line.tiles) { if (t.mesh) { box.expandByObject(t.mesh); any = true; } }
    if (!any) return;
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const halfH = Math.max(10, Math.max(size.x, size.y) / 2 * 1.15);
    const aspect = canvasHostEl.clientWidth / Math.max(1, canvasHostEl.clientHeight);
    camera.left = -halfH * aspect; camera.right = halfH * aspect; camera.top = halfH; camera.bottom = -halfH;
    camera.position.set(center.x, center.y, 100);
    camera.lookAt(center.x, center.y, 0);
    camera.updateProjectionMatrix();
  }

  function disposeScene() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    resizeObs?.disconnect();
    resizeObs = null;
    for (const line of lines) for (const t of line.tiles) disposeTile(t);
    renderer?.dispose();
    if (renderer?.domElement?.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    renderer = null; scene = null; camera = null;
  }

  function disposeTile(t) {
    t.mesh?.geometry?.dispose();
    t.mesh?.material?.map?.dispose();
    t.mesh?.material?.dispose();
    if (t.mesh && scene) scene.remove(t.mesh);
  }

  // ── Tile geometry: quad corners from gdalinfo's cornerCoordinates, UVs matching the image's
  // own top-left-origin convention (ul/ur = row 0 = near/trim-in edge, ll/lr = far edge). ──────
  function buildTileMesh(tile, line) {
    const c = tile.geometry.cornerLonLat;
    if (!c?.ul || !c?.ur || !c?.ll || !c?.lr) return null;
    const ul = toLocalXY(c.ul, localCenter), ur = toLocalXY(c.ur, localCenter);
    const ll = toLocalXY(c.ll, localCenter), lr = toLocalXY(c.lr, localCenter);
    tile.baseCorners = { ul, ur, ll, lr };
    tile.metersPerPx = metersPerAcrossTrackPixel(tile.geometry);

    const geom = new THREE.BufferGeometry();
    const positions = new Float32Array([
      ul[0], ul[1], 0, ur[0], ur[1], 0, ll[0], ll[1], 0,
      ur[0], ur[1], 0, lr[0], lr[1], 0, ll[0], ll[1], 0,
    ]);
    const uvs = new Float32Array([0, 1, 1, 1, 0, 0, 1, 1, 1, 0, 0, 0]);
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geom.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));

    const texture = new THREE.TextureLoader().load(tile.previewUrl);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    const material = new THREE.MeshBasicMaterial({
      map: texture, transparent: true, opacity: line.opacity,
      depthTest: false, depthWrite: false, side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geom, material);
    mesh.renderOrder = line.order;
    return mesh;
  }

  // Recomputes trimmed corners + UVs for every tile whose line has a non-zero trim -- routed
  // through the debounced rAF-loop check above (trimUpdateQueued), not called directly from the
  // slider's input handler, so fast dragging doesn't thrash buffer uploads.
  function applyTrimGeometry() {
    for (const line of lines) {
      for (const t of line.tiles) {
        if (!t.mesh || !t.baseCorners || !t.metersPerPx) continue;
        const { ul, ur, ll, lr } = t.baseCorners;
        // Across-track is the column axis (ul/ll = near/"left" edge, ur/lr = far/"right" edge)
        // -- confirmed visually against the nadir gap's position in the un-rotated preview.
        const totalM = t.metersPerPx * (t.geometry.width || 1);
        const nearFrac = totalM > 0 ? Math.min(0.49, line.trimNearM / totalM) : 0;
        const farFrac = totalM > 0 ? Math.min(0.49, line.trimFarM / totalM) : 0;
        const lerp = (a, b, f) => [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
        const newUl = lerp(ul, ur, nearFrac), newLl = lerp(ll, lr, nearFrac);
        const newUr = lerp(ur, ul, farFrac), newLr = lerp(lr, ll, farFrac);
        const pos = t.mesh.geometry.attributes.position;
        const uv = t.mesh.geometry.attributes.uv;
        const setV = (i, p) => { pos.setXYZ(i, p[0], p[1], 0); };
        setV(0, newUl); setV(1, newUr); setV(2, newLl);
        setV(3, newUr); setV(4, newLr); setV(5, newLl);
        const uvNear = nearFrac, uvFar = 1 - farFrac;
        uv.setXY(0, uvNear, 1); uv.setXY(1, uvFar, 1); uv.setXY(2, uvNear, 0);
        uv.setXY(3, uvFar, 1); uv.setXY(4, uvFar, 0); uv.setXY(5, uvNear, 0);
        pos.needsUpdate = true;
        uv.needsUpdate = true;
      }
    }
  }
  function scheduleTrimUpdate() { trimUpdateQueued = true; }

  // ── Data loading ────────────────────────────────────────────────────────────────────────
  async function loadAvailableFolders(code) {
    loadingFolders = true; renderControls();
    try {
      const r = await fetch(`/api/project/assets?code=${encodeURIComponent(code)}&category=source-data`);
      const d = await r.json();
      // The API's own `folders` field only lists TOP-LEVEL folder names ("Minsas", "Voyis") --
      // confirmed live, it does not include nested subfolder paths. The reliable source for
      // "Minsas/<line>/..." paths is each file's own `name` (full relative path), same approach
      // image-generator's DAM tree uses (derive folders from file paths, not the folders field).
      const files = Array.isArray(d?.files) ? d.files : [];
      const lineNames = new Set();
      for (const f of files) {
        const name = String(f?.name || "");
        if (!name.startsWith(MINSAS_FOLDER_PREFIX)) continue;
        const rest = name.slice(MINSAS_FOLDER_PREFIX.length);
        const slash = rest.indexOf("/");
        if (slash > 0) lineNames.add(rest.slice(0, slash));
      }
      availableFolders = [...lineNames].sort();
    } catch {
      availableFolders = [];
    } finally {
      loadingFolders = false; renderControls();
    }
  }

  async function loadLine(folder) {
    if (findLine(folder)) return;
    const line = {
      folder, included: true, opacity: 1, trimNearM: 0, trimFarM: 0,
      order: lines.length, loading: true, error: null, tiles: [],
    };
    lines.push(line);
    renderControls();
    try {
      const r = await fetch(`/api/project/minsas/line-tiles?code=${encodeURIComponent(currentCode)}&folder=${encodeURIComponent(MINSAS_FOLDER_PREFIX + folder)}`);
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`);
      const tiles = (d.tiles || []).filter((t) => !t.error && t.geometry !== false);
      if (!localCenter) {
        const first = tiles.find((t) => t.cornerLonLat?.ul);
        if (first) localCenter = [...first.cornerLonLat.ul];
      }
      line.tiles = tiles.map((t) => ({ key: t.key, geometry: t, previewUrl: t.previewUrl, mesh: null }));
      ensureScene();
      for (const t of line.tiles) {
        const mesh = buildTileMesh(t, line);
        if (mesh) { t.mesh = mesh; scene.add(mesh); }
      }
      fitToTiles();
    } catch (err) {
      line.error = String(err?.message || err);
    } finally {
      line.loading = false;
      renderControls();
    }
  }

  function removeLine(folder) {
    const idx = lines.findIndex((l) => l.folder === folder);
    if (idx === -1) return;
    const [line] = lines.splice(idx, 1);
    for (const t of line.tiles) disposeTile(t);
    selectedLineIds.delete(folder);
    renderControls();
  }

  // ── Selection + mixed-state controls ───────────────────────────────────────────────────
  function handleLineRowClick(folder, ev) {
    if (ev.shiftKey && lastClickedLineId) {
      const ids = lines.map((l) => l.folder);
      const a = ids.indexOf(lastClickedLineId), b = ids.indexOf(folder);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        selectedLineIds = new Set(ids.slice(lo, hi + 1));
      }
    } else if (ev.metaKey || ev.ctrlKey) {
      if (selectedLineIds.has(folder)) selectedLineIds.delete(folder); else selectedLineIds.add(folder);
    } else {
      selectedLineIds = new Set([folder]);
    }
    lastClickedLineId = folder;
    renderControls();
  }

  function mixedValue(key) {
    const sel = selectedLines();
    if (!sel.length) return { value: null, mixed: false };
    const first = sel[0][key];
    const mixed = sel.some((l) => l[key] !== first);
    return { value: mixed ? null : first, mixed };
  }

  function setSelectedValue(key, value) {
    for (const line of selectedLines()) line[key] = value;
  }

  // ── Bake ────────────────────────────────────────────────────────────────────────────────
  async function startBake() {
    if (!lines.some((l) => l.included)) { app.ui.toast("Include at least one line before baking."); return; }
    const body = {
      code: currentCode,
      name: bakeName || undefined,
      blendMode: "over",
      lines: lines.map((l) => ({
        folder: MINSAS_FOLDER_PREFIX + l.folder, included: l.included,
        opacity: l.opacity, trimNearM: l.trimNearM, trimFarM: l.trimFarM, order: l.order,
      })),
    };
    try {
      const r = await fetch("/api/project/minsas/mosaic/bake", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`);
      bakeJobId = d.jobId;
      bakeStatus = { status: "queued" };
      app.ui.toast(`Baking ${d.lineCount} line(s)…`);
      pollBake();
    } catch (err) {
      app.ui.toast(`Bake failed: ${err?.message || err}`);
    }
    renderControls();
  }

  function pollBake() {
    clearTimeout(bakePollTimer);
    bakePollTimer = setTimeout(async () => {
      try {
        const r = await fetch(`/api/project/minsas/mosaic/bake/${encodeURIComponent(bakeJobId)}`);
        const d = await r.json();
        bakeStatus = d;
        if (d.status === "completed") {
          app.ui.toast(`Bake complete: ${d.targetKey?.split("/").pop() || "new file"}`);
          bakeJobId = null; renderControls(); return;
        }
        if (d.status === "failed") {
          app.ui.toast(`Bake failed: ${d.error || "unknown error"}`);
          bakeJobId = null; renderControls(); return;
        }
      } catch {}
      renderControls();
      pollBake();
    }, 1500);
  }

  // ── UI ──────────────────────────────────────────────────────────────────────────────────
  function trimSliderHtml(label, key, max) {
    const { value, mixed } = mixedValue(key);
    return `
      <label class="small" style="display:flex;flex-direction:column;gap:2px;">
        <span>${esc(label)}${mixed ? ` <em style="color:var(--muted);">(mixed)</em>` : ""} ${mixed ? "" : `— ${(value || 0).toFixed(1)} m`}</span>
        <input type="range" data-ctrl="${key}" min="0" max="${max}" step="0.1" value="${mixed ? 0 : value || 0}" style="width:220px;" />
      </label>`;
  }

  function renderLineList() {
    const host = containerEl?.querySelector("[data-line-list-host]");
    if (!host) return;
    host.innerHTML = lines.length === 0
      ? `<div class="small" style="color:var(--muted);padding:6px 0;">No lines loaded yet — pick one above.</div>`
      : [...lines].sort((a, b) => a.order - b.order).map((line) => {
          const sel = selectedLineIds.has(line.folder);
          return `
            <div data-line-row="${esc(line.folder)}" draggable="true" style="display:flex;align-items:center;gap:6px;padding:4px 6px;border-radius:4px;cursor:pointer;background:${sel ? "rgba(56,189,248,0.15)" : "transparent"};border:1px solid ${sel ? "rgba(56,189,248,0.4)" : "transparent"};">
              <span style="font-size:9px;color:var(--muted);cursor:grab;">⠿</span>
              <input type="checkbox" data-line-include="${esc(line.folder)}" ${line.included ? "checked" : ""} title="Include in bake" />
              <span class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(line.folder)}</span>
              ${line.loading ? `<span class="small" style="color:var(--muted);">loading…</span>` : ""}
              ${line.error ? `<span class="small" style="color:#f87171;" title="${esc(line.error)}">error</span>` : `<span class="small" style="color:var(--muted);">${line.tiles.length} tiles</span>`}
              <button type="button" data-line-remove="${esc(line.folder)}" class="btn" style="font-size:9px;padding:1px 5px;color:#f87171;">✕</button>
            </div>`;
        }).join("");
  }

  function renderControls() {
    if (!containerEl) return;
    const host = containerEl.querySelector("[data-controls-host]");
    const unloaded = availableFolders.filter((f) => !findLine(f));
    if (host) {
      const sel = selectedLines();
      host.innerHTML = `
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding-bottom:6px;">
          <select data-add-line style="font-size:11px;">
            <option value="">${loadingFolders ? "Loading lines…" : "+ Add line…"}</option>
            ${unloaded.map((f) => `<option value="${esc(f)}">${esc(f)}</option>`).join("")}
          </select>
        </div>
        ${sel.length ? `
          <div style="border-top:1px solid rgba(148,163,184,0.15);padding-top:8px;display:flex;flex-direction:column;gap:8px;">
            <div class="small" style="color:var(--muted);">${sel.length} line(s) selected</div>
            <label class="small" style="display:flex;flex-direction:column;gap:2px;">
              <span>Opacity${mixedValue("opacity").mixed ? ` <em style="color:var(--muted);">(mixed)</em>` : ` — ${Math.round((mixedValue("opacity").value ?? 1) * 100)}%`}</span>
              <input type="range" data-ctrl="opacity" min="0" max="1" step="0.01" value="${mixedValue("opacity").mixed ? 1 : mixedValue("opacity").value}" style="width:220px;" />
            </label>
            ${trimSliderHtml("Trim in (near edge)", "trimNearM", 50)}
            ${trimSliderHtml("Trim in (far edge)", "trimFarM", 50)}
            <label class="small" style="display:flex;gap:6px;align-items:center;">
              Blend mode
              <select disabled style="font-size:11px;"><option>Over (draw order)</option></select>
              <span style="color:var(--muted);">— more modes in a later phase</span>
            </label>
          </div>
        ` : `<div class="small" style="color:var(--muted);padding-top:4px;">Select one or more lines to adjust them.</div>`}
        <div style="border-top:1px solid rgba(148,163,184,0.15);margin-top:8px;padding-top:8px;display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <input type="text" data-bake-name placeholder="mosaic name (optional)" value="${esc(bakeName)}" style="font-size:11px;flex:1;min-width:140px;" />
          <button type="button" data-bake-btn class="btn" ${bakeJobId ? "disabled" : ""}>${bakeJobId ? "Baking…" : "Bake Mosaic"}</button>
          ${bakeJobId && bakeStatus ? `<span class="small" style="color:var(--muted);">${esc(bakeStatus.status || "")}${bakeStatus.progress?.total ? ` (${bakeStatus.progress.done}/${bakeStatus.progress.total})` : ""}</span>` : ""}
        </div>
      `;
      wireControlEvents(host);
    }
    renderLineList();
  }

  function wireControlEvents(host) {
    host.querySelector("[data-add-line]")?.addEventListener("change", (e) => {
      const folder = e.target.value;
      if (folder) loadLine(folder);
      e.target.value = "";
    });
    host.querySelectorAll("[data-ctrl]").forEach((input) => {
      input.addEventListener("input", () => {
        const key = input.getAttribute("data-ctrl");
        const val = Number(input.value);
        setSelectedValue(key, val);
        if (key === "opacity") {
          for (const line of selectedLines()) for (const t of line.tiles) if (t.mesh) t.mesh.material.opacity = val;
        } else {
          scheduleTrimUpdate();
        }
      });
    });
    host.querySelector("[data-bake-name]")?.addEventListener("input", (e) => { bakeName = e.target.value; });
    host.querySelector("[data-bake-btn]")?.addEventListener("click", startBake);
  }

  function wireLineListEvents() {
    const host = containerEl?.querySelector("[data-line-list-host]");
    if (!host) return;
    host.addEventListener("click", (ev) => {
      const removeBtn = ev.target.closest?.("[data-line-remove]");
      if (removeBtn) { removeLine(removeBtn.getAttribute("data-line-remove")); return; }
      const checkbox = ev.target.closest?.("[data-line-include]");
      if (checkbox) {
        const line = findLine(checkbox.getAttribute("data-line-include"));
        if (line) line.included = checkbox.checked;
        return;
      }
      const row = ev.target.closest?.("[data-line-row]");
      if (row) handleLineRowClick(row.getAttribute("data-line-row"), ev);
    });
    // Drag-to-reorder: plain HTML5 DnD, swaps `order` values on drop.
    let dragFolder = null;
    host.addEventListener("dragstart", (ev) => {
      const row = ev.target.closest?.("[data-line-row]");
      dragFolder = row?.getAttribute("data-line-row") || null;
    });
    host.addEventListener("dragover", (ev) => { if (dragFolder) ev.preventDefault(); });
    host.addEventListener("drop", (ev) => {
      ev.preventDefault();
      const row = ev.target.closest?.("[data-line-row]");
      const targetFolder = row?.getAttribute("data-line-row");
      if (!dragFolder || !targetFolder || dragFolder === targetFolder) return;
      const ordered = [...lines].sort((a, b) => a.order - b.order);
      const from = ordered.findIndex((l) => l.folder === dragFolder);
      const to = ordered.findIndex((l) => l.folder === targetFolder);
      if (from === -1 || to === -1) return;
      const [moved] = ordered.splice(from, 1);
      ordered.splice(to, 0, moved);
      ordered.forEach((l, i) => { l.order = i; for (const t of l.tiles) if (t.mesh) t.mesh.renderOrder = i; });
      dragFolder = null;
      renderControls();
    });
  }

  function renderSkeleton() {
    containerEl.innerHTML = `
      <div class="small" style="color:var(--muted);padding-bottom:8px;">
        Load several MinSAS survey lines, select one or many (shift/ctrl-click, or drag to
        reorder), adjust opacity and across-track range trim live, then bake a real mosaic
        GeoTIFF. Draw order (top of the list) wins where lines overlap.
      </div>
      ${!currentCode ? `<div class="small" style="color:#f59e0b;padding:8px 0;">Select a project first.</div>` : `
        <div style="display:grid;grid-template-columns:260px 1fr;gap:10px;height:560px;">
          <div style="display:flex;flex-direction:column;gap:8px;overflow:auto;">
            <div data-controls-host></div>
            <div style="border-top:1px solid rgba(148,163,184,0.15);padding-top:6px;">
              <div style="font-weight:600;font-size:11px;margin-bottom:4px;">Lines</div>
              <div data-line-list-host style="display:flex;flex-direction:column;gap:3px;"></div>
            </div>
          </div>
          <div data-canvas-host style="border:1px solid rgba(148,163,184,0.15);border-radius:4px;overflow:hidden;background:#020617;"></div>
        </div>
      `}
    `;
    if (currentCode) {
      canvasHostEl = containerEl.querySelector("[data-canvas-host]");
      wireLineListEvents();
      ensureScene();
      renderControls();
    }
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────────────────
  function onProjectChanged(e) {
    const code = String(e.detail?.projectId || "");
    if (code === currentCode) return;
    currentCode = code;
    disposeScene();
    lines = []; selectedLineIds = new Set(); localCenter = null;
    availableFolders = []; bakeJobId = null; bakeStatus = null;
    clearTimeout(bakePollTimer);
    renderSkeleton();
    if (currentCode) loadAvailableFolders(currentCode);
  }

  function mount(nextContainer) {
    containerEl = nextContainer;
    if (!currentCode && _lastBroadcastProjectId) currentCode = _lastBroadcastProjectId;
    document.addEventListener("hydrotools:project-changed", onProjectChanged);
    renderSkeleton();
    if (currentCode) loadAvailableFolders(currentCode);
  }

  function destroy() {
    document.removeEventListener("hydrotools:project-changed", onProjectChanged);
    clearTimeout(bakePollTimer);
    disposeScene();
    containerEl = null; canvasHostEl = null;
  }

  return { mount, destroy };
}

export const sonarMosaicEditorTool = {
  id: TOOL_ID,
  name: "Sonar Mosaic Editor",
  icon: "&#128266;",
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};
